import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight, { type Options as HighlightOptions } from 'rehype-highlight';

import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import diff from 'highlight.js/lib/languages/diff';
import go from 'highlight.js/lib/languages/go';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import python from 'highlight.js/lib/languages/python';
import rust from 'highlight.js/lib/languages/rust';
import shell from 'highlight.js/lib/languages/shell';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';

interface MarkdownRendererProps {
  source: string;
  /**
   * Whether the markdown body is "settled" (final). When false (the
   * streaming chat path), highlighting falls back to a no-op so we
   * don't repeatedly walk the AST through highlight.js on every delta.
   * Once the assistant turn finishes the parent flips this to true and
   * the cells re-render once with full highlighting.
   */
  highlight?: boolean;
}

/**
 * Curated language registry. `detect: true` (auto-detect any language)
 * is expensive when the markdown body is re-parsed on every streaming
 * delta — and our chat replies are dominated by ~10 common languages.
 * Listing them explicitly with `detect: false` keeps the per-block
 * highlight cost bounded to a single language match per fenced block.
 */
const HIGHLIGHT_OPTIONS: HighlightOptions = {
  detect: false,
  languages: {
    bash,
    sh: shell,
    shell,
    css,
    diff,
    go,
    js: javascript,
    javascript,
    json,
    markdown,
    md: markdown,
    py: python,
    python,
    rust,
    sql,
    ts: typescript,
    tsx: typescript,
    typescript,
    xml,
    html: xml,
    yaml,
    yml: yaml,
  },
};

const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [[rehypeHighlight, HIGHLIGHT_OPTIONS]] as const;
const REHYPE_PLUGINS_NO_HIGHLIGHT: never[] = [];

const COMPONENTS = {
  a({ href, children, ...rest }: React.ComponentProps<'a'>) {
    return (
      <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
  table({ children, ...rest }: React.ComponentProps<'table'>) {
    return (
      <div className="markdown-table-wrap">
        <table {...rest}>{children}</table>
      </div>
    );
  },
};

/**
 * Shared markdown renderer for chat messages and the file viewer.
 * GFM tables / strikethrough / task-lists, syntax highlighting via
 * highlight.js. No raw HTML is rendered (react-markdown's default), so
 * the agent's output cannot inject scripts.
 *
 * Memoised on the raw source so a streaming chat message only
 * re-renders when the text actually changes (which is fine because the
 * AI SDK delivers a stable string per delta).
 *
 * Highlighting is OFF during streaming (`highlight={false}`) and ON
 * for settled content (file viewer, finished assistant turns) — the
 * caller flips the flag, not us.
 */
export const MarkdownRenderer = memo(function MarkdownRenderer({
  source,
  highlight = true,
}: MarkdownRendererProps) {
  // The plugins array is intentionally a stable module-scope reference
  // when highlighting is enabled; toggling rebuilds it once per flip,
  // not per delta.
  const rehypePlugins = (highlight ? REHYPE_PLUGINS : REHYPE_PLUGINS_NO_HIGHLIGHT) as
    | typeof REHYPE_PLUGINS
    | never[];
  return (
    <div className="markdown">
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={rehypePlugins as never}
        components={COMPONENTS}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
});
